import { z } from 'zod';
import { TransactionTypeSchema } from '../enums';

// Base Account Schema
export const BaseAccountSchema = z.object({
  accountId: z.string().optional(), // MongoDB ObjectId
  accountNumber: z.string().min(1, 'Account number is required').max(50, 'Account number too long'),
  accountName: z.string().min(1, 'Account name is required').max(100, 'Account name too long'),
  balance: z.number().default(0), // Current balance
  lastUpdated: z.date().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type BaseAccount = z.infer<typeof BaseAccountSchema>;

// Account Creation Schema
export const CreateAccountSchema = BaseAccountSchema.omit({
  accountId: true,
  balance: true,
  lastUpdated: true,
  createdAt: true,
  updatedAt: true
});

export type CreateAccount = z.infer<typeof CreateAccountSchema>;

// Account Update Schema
export const UpdateAccountSchema = BaseAccountSchema.partial().extend({
  accountId: z.string() // Required for updates
});

export type UpdateAccount = z.infer<typeof UpdateAccountSchema>;

// Account Response Schema (for API responses)
export const AccountResponseSchema = BaseAccountSchema.extend({
  accountId: z.string()
});

export type AccountResponse = z.infer<typeof AccountResponseSchema>;

// Account with Balance Schema
export const AccountWithBalanceSchema = AccountResponseSchema.extend({
  currentBalance: z.number(),
  lastTransactionDate: z.date().optional(),
  transactionCount: z.number().int().min(0).optional()
});

export type AccountWithBalance = z.infer<typeof AccountWithBalanceSchema>;

// Account List Query Schema
export const AccountListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  isActive: z.boolean().optional(),
  minBalance: z.number().optional(),
  maxBalance: z.number().optional(),
  sortBy: z.enum(['accountNumber', 'accountName', 'balance', 'lastUpdated']).default('accountNumber'),
  sortOrder: z.enum(['asc', 'desc']).default('asc')
});

export type AccountListQuery = z.infer<typeof AccountListQuerySchema>;

// Account Transaction Schema
export const AccountTransactionSchema = z.object({
  transactionId: z.string().optional(), // MongoDB ObjectId
  transactionType: TransactionTypeSchema,
  accountId: z.string(), // Reference to AccountID
  amount: z.number().positive('Amount must be positive'),
  slipNo: z.string().optional(),
  description: z.string().max(500, 'Description too long').optional(),
  createdBy: z.string(), // Reference to UserID
  time: z.date().optional(), // Server time when recorded
  deviceId: z.string().min(1, 'Device ID is required'),
  officeNo: z.number().int().positive('Office number must be positive'),
  regionId: z.number().int().positive('Region ID must be positive'),
  relatedTransactionId: z.string().optional(), // For linked transactions (e.g., expense)
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type AccountTransaction = z.infer<typeof AccountTransactionSchema>;

// Account Transaction Creation Schema
export const CreateAccountTransactionSchema = AccountTransactionSchema.omit({
  transactionId: true,
  time: true,
  createdAt: true,
  updatedAt: true
});

export type CreateAccountTransaction = z.infer<typeof CreateAccountTransactionSchema>;

// Account Transaction Response Schema
export const AccountTransactionResponseSchema = AccountTransactionSchema.extend({
  transactionId: z.string()
});

export type AccountTransactionResponse = z.infer<typeof AccountTransactionResponseSchema>;

// Account Transaction with Relations Schema
export const AccountTransactionWithRelationsSchema = AccountTransactionResponseSchema.extend({
  account: z.object({
    accountId: z.string(),
    accountName: z.string(),
    accountNumber: z.string()
  }).optional(),
  office: z.object({
    officeNo: z.number(),
    officeName: z.string(),
    regionId: z.number(),
    regionName: z.string()
  }).optional(),
  createdByUser: z.object({
    userId: z.string(),
    userName: z.string(),
    userRole: z.string()
  }).optional()
});

export type AccountTransactionWithRelations = z.infer<typeof AccountTransactionWithRelationsSchema>;

// Account Transaction List Query Schema
export const AccountTransactionListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  accountId: z.string().optional(),
  transactionType: TransactionTypeSchema.optional(),
  officeNo: z.number().int().positive().optional(),
  regionId: z.number().int().positive().optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  minAmount: z.number().positive().optional(),
  maxAmount: z.number().positive().optional(),
  createdBy: z.string().optional(),
  sortBy: z.enum(['time', 'amount', 'transactionType', 'createdAt']).default('time'),
  sortOrder: z.enum(['asc', 'desc']).default('desc')
});

export type AccountTransactionListQuery = z.infer<typeof AccountTransactionListQuerySchema>;

// Account Balance Update Schema
export const AccountBalanceUpdateSchema = z.object({
  accountId: z.string(),
  newBalance: z.number(),
  transactionId: z.string(),
  reason: z.string().min(1, 'Reason is required')
});

export type AccountBalanceUpdate = z.infer<typeof AccountBalanceUpdateSchema>;

// Account Statement Schema
export const AccountStatementSchema = z.object({
  accountId: z.string(),
  accountName: z.string(),
  accountNumber: z.string(),
  startBalance: z.number(),
  endBalance: z.number(),
  transactions: z.array(AccountTransactionWithRelationsSchema),
  period: z.object({
    start: z.date(),
    end: z.date()
  })
});

export type AccountStatement = z.infer<typeof AccountStatementSchema>; 